'use client';

import { CALENDLY_URL } from '@/lib/config';
import { createCalendlyOpener } from '@/utils/calendlyOpener';
import { useCallback, useMemo, useRef, useState } from 'react';

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (opts: { url: string }) => void;
    };
  }
}

const WIDGET_JS = 'https://assets.calendly.com/assets/external/widget.js';
const WIDGET_CSS = 'https://assets.calendly.com/assets/external/widget.css';

function ensureCalendlyAssets() {
  if (!document.querySelector(`link[href="${WIDGET_CSS}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = WIDGET_CSS;
    document.head.appendChild(link);
  }
  if (!document.querySelector(`script[src="${WIDGET_JS}"]`)) {
    const script = document.createElement('script');
    script.src = WIDGET_JS;
    script.async = true;
    document.head.appendChild(script);
  }
}

// Fixed white theme — always light regardless of app dark/light mode.
const CALENDLY_THEME_PARAMS = new URLSearchParams({
  background_color: 'ffffff',
  text_color: '111827',
  primary_color: '15703f', // --brand-primary (light mode, Advocate's Lamp default)
}).toString();

/**
 * Returns `openCalendly()` (plus an `isLoading` flag for the button) that loads Calendly's widget only when the
 * visitor clicks to book a call.
 *
 * Never preload on mount (#11843): that fetched assets.calendly.com on every
 * page carrying the footer or sidebar, sending the visitor's IP and device
 * data to Calendly, LLC before any consent and without the visitor asking for
 * anything. Loaded on the click, the embed is part of a service the visitor
 * explicitly requested, which is the "strictly necessary" exemption of
 * § 109 ods. 8 zákona č. 452/2021 Z. z. / ePrivacy čl. 5 ods. 3 (Article 29
 * WP Opinion 04/2012 on Cookie Consent Exemption, WP194, criterion B).
 */
export function useCalendlyPopup(): {
  openCalendly: () => void;
  isLoading: boolean;
} {
  const [isLoading, setIsLoading] = useState(false);
  // One opener per mounted component, so its in-flight guard survives renders.
  const openerRef = useRef<(() => void) | null>(null);
  const openCalendly = useCallback(() => {
    openerRef.current ??= createCalendlyOpener(
      `${CALENDLY_URL}?${CALENDLY_THEME_PARAMS}`,
      {
        ensureAssets: ensureCalendlyAssets,
        getCalendly: () => window.Calendly,
        schedule: (task, delayMs) => {
          setTimeout(task, delayMs);
        },
        // The widget never loaded (blocker, offline): open the booking page
        // itself rather than leave the click doing nothing.
        onGiveUp: (url) => {
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        onLoadingChange: setIsLoading,
      }
    );
    openerRef.current();
  }, []);
  return useMemo(
    () => ({ openCalendly, isLoading }),
    [openCalendly, isLoading]
  );
}
